import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import {getCheckoutPageData} from '@/server/checkout';
import {loadStripe} from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {useState, useCallback} from 'react';
import {Button} from '@/components/ui/button';
import {Typography} from '@/components/ui/typography';
import {Logo} from '@/components/ui/logo/logo';
import {formatDate, formatTime} from '@/utils/displayFormatUtils';
import {Loader2} from 'lucide-react';
import {formatPrice} from '@/utils/currencyUtils';
import {Category} from '@/apiTypes/event';
import {HoldTimer} from '@/components/checkout/holdTimer';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export const Route = createFileRoute('/checkout/$registrationId')({
  beforeLoad: ({context}) => {
    if (!context.featureFlags.stripe_enabled) {
      throw redirect({to: '/'});
    }
  },
  loader: async ({params}) => {
    const pageData = await getCheckoutPageData({
      data: {registrationId: params.registrationId},
    });

    if (pageData.status === 'paid' && pageData.event) {
      throw redirect({
        to: '/event/$eventId',
        params: {eventId: pageData.event.id},
      });
    }

    return pageData;
  },
  component: CheckoutPage,
});

/** Minimal two-column checkout: payment form on left, order summary on right. */
function CheckoutPage() {
  const pageData = Route.useLoaderData();
  const navigate = useNavigate();
  const [isExpired, setIsExpired] = useState(false);
  const handleExpired = useCallback(() => setIsExpired(true), []);

  const {
    status,
    event,
    category,
    routeName,
    routeCount,
    fieldCount,
    amountCents,
    currency,
    billingDetails,
  } = pageData;

  if (status === 'expired' || isExpired) {
    return (
      <CheckoutShell>
        <div className="flex flex-col items-center justify-center flex-1 gap-4 p-8">
          <Typography level="H3">Hold Expired</Typography>
          <Typography level="B2" className="text-neutral-600">
            Your spot reservation has expired. Please return to the event page
            to try again.
          </Typography>
          <Button
            onClick={() =>
              navigate({
                to: '/event/$eventId',
                params: {eventId: event.id},
              })
            }
          >
            Back to Event
          </Button>
        </div>
      </CheckoutShell>
    );
  }

  if (status === 'invalid' || !pageData.clientSecret) {
    return (
      <CheckoutShell>
        <div className="flex items-center justify-center flex-1 p-8">
          <Typography level="B2" className="text-error-700">
            Checkout state is incomplete. Please return to the event page and
            try again.
          </Typography>
        </div>
      </CheckoutShell>
    );
  }
  return (
    <CheckoutShell>
      <div className="flex flex-col lg:flex-row flex-1">
        {/* Left column — Payment form */}
        <div className="flex-1 flex flex-col p-6 lg:p-12 lg:max-w-xl mx-auto w-full">
          <div className="flex items-center justify-between mb-8">
            <Typography level="H3">Checkout</Typography>
            {'holdExpiresAt' in pageData && pageData.holdExpiresAt && (
              <HoldTimer
                holdExpiresAt={pageData.holdExpiresAt}
                onExpired={handleExpired}
              />
            )}
          </div>
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: pageData.clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  borderRadius: '8px',
                },
              },
            }}
          >
            <CheckoutForm
              eventId={event.id}
              amountCents={amountCents}
              currency={currency}
              billingName={billingDetails.name}
              billingEmail={billingDetails.email}
            />
          </Elements>
        </div>
        {/* Right column — Order summary */}
        <div className="w-full lg:w-[420px] p-6 lg:p-12 bg-neutral-50 border-t lg:border-t-0 lg:border-l border-neutral-200 order-first lg:order-last">
          <OrderSummary
            event={event}
            category={category}
            routeName={routeName}
            routeCount={routeCount}
            fieldCount={fieldCount}
            amountCents={amountCents}
            currency={currency}
          />
        </div>
      </div>
    </CheckoutShell>
  );
}

interface OrderSummaryProps {
  event: {
    id: string;
    name: string;
    startDate: Date | null;
    heroUrl: string | null;
  };
  category: Pick<Category, 'id' | 'name' | 'startTime'>;
  routeName: string | null;
  routeCount: number;
  fieldCount: number;
  amountCents: number | null;
  currency: string | null;
}

/** Displays the event details and price for the checkout sidebar. */
function OrderSummary({
  event,
  category,
  routeName,
  routeCount,
  fieldCount,
  amountCents,
  currency,
}: OrderSummaryProps) {
  const showRouteName = routeName && routeCount > 1;
  const showFieldName = fieldCount > 1;
  return (
    <div className="flex flex-col gap-6">
      {event.heroUrl && (
        <img
          src={event.heroUrl}
          alt={event.name}
          className="w-full h-48 object-cover rounded-lg"
        />
      )}

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-1">
          <Typography level="H4">{event.name}</Typography>
          <div className="flex gap-2">
            {event.startDate && (
              <Typography level="B3" className="text-neutral-500">
                {formatDate(event.startDate)}
              </Typography>
            )}
            {category.startTime && event.startDate && (
              <Typography level="B3" className="text-neutral-500">
                ·
              </Typography>
            )}
            {category.startTime && (
              <Typography level="B3" className="text-neutral-500">
                {formatTime(category.startTime)}
              </Typography>
            )}
          </div>
        </div>
        {(showRouteName || showFieldName) && (
          <div className="flex flex-col gap-1">
            {showRouteName && (
              <div className="div flex justify-between">
                <Typography level="B1" className="text-neutral-500">
                  Route
                </Typography>
                <Typography level="S1" className="text-neutral-600">
                  {routeName}
                </Typography>
              </div>
            )}
            {showFieldName && (
              <div className="div flex justify-between">
                <Typography level="B1" className="text-neutral-500">
                  Field
                </Typography>
                <Typography level="S1" className="text-neutral-600">
                  {category.name}
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-neutral-200 pt-4">
        <div className="flex justify-between items-end">
          <Typography level="B1" className="text-neutral-500">
            Total
          </Typography>
          <Typography level="H4" className="font-black italic">
            {amountCents
              ? formatPrice(amountCents, {precise: true, currency: currency ?? undefined})
              : 'Free'}
          </Typography>
        </div>
      </div>
    </div>
  );
}

interface CheckoutFormProps {
  eventId: string;
  amountCents: number | null;
  currency: string | null;
  billingName: string | null;
  billingEmail: string;
}

/** Card-only payment form using Stripe PaymentElement with all non-card fields hidden. */
function CheckoutForm({
  eventId,
  amountCents,
  currency,
  billingName,
  billingEmail,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const router = useRouter();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements) return;

      setIsProcessing(true);
      setErrorMessage(null);

      // Stripe throws (rather than returning `error`) on integration
      // errors, so catch to keep the form from spinning forever
      let error: {message?: string} | undefined;
      try {
        ({error} = await stripe.confirmPayment({
          elements,
          redirect: 'if_required',
          confirmParams: {
            payment_method_data: {
              billing_details: {
                // Omitted when unknown — the PaymentElement collects it instead
                name: billingName ?? undefined,
                email: billingEmail,
                phone: '',
              },
            },
          },
        }));
      } catch (err) {
        error = {message: err instanceof Error ? err.message : undefined};
      }

      if (error) {
        setErrorMessage(error.message ?? 'An unexpected error occurred.');
        setIsProcessing(false);
      } else {
        await router.invalidate();
        navigate({
          to: '/event/$eventId',
          params: {eventId},
        });
      }
    },
    [stripe, elements, eventId, router, navigate, billingName, billingEmail]
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <PaymentElement
        options={{
          fields: {
            billingDetails: {
              // Collect the name in the form when the profile doesn't have one
              name: billingName ? 'never' : 'auto',
              email: 'never',
              phone: 'never',
            },
          },
          wallets: {
            applePay: 'never',
            googlePay: 'never',
          },
        }}
      />

      {errorMessage && (
        <Typography level="B3" className="text-error-700">
          {errorMessage}
        </Typography>
      )}

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={!stripe || isProcessing}
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : amountCents ? (
          `Pay ${formatPrice(amountCents, {precise: true, currency: currency ?? undefined})}`
        ) : (
          'Pay'
        )}
      </Button>
      <Link
        to="/event/$eventId"
        params={{eventId}}
        className="text-center text-neutral-500 hover:text-neutral-800 transition-colors"
      >
        <Typography level="B3">Cancel</Typography>
      </Link>
    </form>
  );
}

/** Minimal page shell with just the Wayra logo. */
function CheckoutShell({children}: {children: React.ReactNode}) {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="h-16 flex items-center justify-center px-6 border-b border-neutral-200">
        <Logo className="h-6" />
      </div>
      {children}
    </div>
  );
}
